import React, { useMemo, useEffect } from "react";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Label,
  Col,
  Row,
} from "reactstrap";
import { useCustomContext } from "../../_helper/customizer";
import { Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { AntSelect } from "../AntSelect";
import { AntInput } from "../AntInput";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions/SelectCachedOptions";

export const GenericFilterModal = ({
  isOpen,
  toggle,
  onApply,
  filters = {},
  filterFields = [],
  onReset,
}) => {
  const { layout } = useCustomContext();
  const { t } = useTranslation();
  const { control, handleSubmit, reset, watch, setValue } = useForm();

  // Build values from filterFields + applied filters (parent can omit keys after e.g. clearing mapping).
  // Depend only on `filters`: `filterFields` is often a new array each parent render; including it would
  // recreate defaults every render and re-run reset while the modal is open (broken UI + duplicate queries).
  const defaultValues = useMemo(() => {
    const readWatch = (name) => filters[name];
    const values = {};
    filterFields.forEach((field) => {
      const fieldObject =
        typeof field === "function" ? field(readWatch) : field;
      const { name } = fieldObject;
      if (!(name in filters)) {
        values[name] = fieldObject.defaultValue ?? null;
      } else {
        const v = filters[name];
        values[name] = v === "" || v === undefined ? null : v;
      }
    });
    return values;
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps -- filterFields omitted (unstable ref per render)

  const filtersSyncKey = useMemo(
    () => JSON.stringify(filters == null ? {} : filters),
    [filters]
  );

  // Reset when modal opens or when applied filters change (defaultValues tracks `filters` only).
  useEffect(() => {
    if (isOpen) {
      reset(defaultValues);
    }
  }, [isOpen, filtersSyncKey, reset, defaultValues]);

  const title = ({ children }) => (
    <h5 className="w-100 d-flex align-items-center justify-content-center gap-2">
      {children}
    </h5>
  );

  const dir = layout === "rtl" ? "ltr" : "rtl";

  const handleCancel = () => {
    reset(defaultValues);
    toggle();
  };

  const onSubmit = (data) => {
    // Filter out empty values
    const filteredData = Object.fromEntries(
      Object.entries(data).filter(
        ([_, value]) => value !== "" && value !== null && value !== undefined
      )
    );

    onApply(filteredData);
    toggle();
  };

  const handleReset = () => {
    // Reset to empty values, not to current filters
    const emptyValues = {};
    filterFields.forEach((field) => {
      const fieldObject = typeof field === "function" ? field(watch, setValue) : field
      emptyValues[fieldObject.name] = fieldObject.defaultValue || null;
    });
    reset(emptyValues);
    onReset?.(emptyValues);
  };

  // Split input rendering into separate functions for each type
  const renderSelectInput = ({
    controllerField,
    name,
    label,
    placeholder,
    options,
    onChange,
    optionKey,
    ...restFieldProps
  }) => {
    const commonProps = {
      id: name,
      ...controllerField,
      ...restFieldProps,
      showSearch: true,
      allowClear: true,
      onChange: (value) => {
        controllerField.onChange(value ?? null);
        onChange?.(value ?? null);
      },
      placeholder:
        typeof placeholder === "function"
          ? placeholder()
          : placeholder || label,
      options: options || [],
    };

    if (!optionKey) {
      return (
        <AntSelect
          {...commonProps}
          options={typeof options === "function" ? [] : options}
        />
      );
    }

    return <SelectCachedOptions {...commonProps} optionKey={optionKey} />;
  };

  const renderTextInput = ({
    controllerField,
    name,
    label,
    placeholder,
    onChange,
    inputType,
    ...restFieldProps
  }) => {
    return (
      <AntInput
        {...controllerField}
        {...restFieldProps}
        onChange={(value) => {
          controllerField.onChange(value);
          onChange?.(value);
        }}
        id={name}
        type={inputType || "text"}
        placeholder={
          typeof placeholder === "function"
            ? placeholder()
            : placeholder || label
        }
        allowClear
      />
    );
  };

  const renderDateInput = ({
    controllerField,
    name,
    label,
    placeholder,
    onChange,
    dateFormat,
    showTime,
    ...restFieldProps
  }) => {
    return (
      <DatePicker
        {...restFieldProps}
        style={{
          ...(restFieldProps?.style ?? {}),
          height: "50px",
        }}
        value={controllerField.value ? dayjs(controllerField.value) : null}
        onChange={(date) => {
          const formattedDate = date
            ? dayjs(date).format("YYYY-MM-DD HH:mm:ss")
            : null;
          controllerField.onChange(formattedDate);
          onChange?.(date);
        }}
        id={name}
        className="w-100"
        placeholder={
          typeof placeholder === "function"
            ? placeholder()
            : placeholder || label
        }
        format={dateFormat || "YYYY-MM-DD"}
        showTime={showTime}
        allowClear
      />
    );
  };

  const renderField = (field) => {
    const { colSize = { md: 6 }, ...fieldProps } = field;

    const {
      name,
      label,
      type,
      placeholder,
      options,
      onChange,
      inputType,
      dateFormat,
      showTime,
      render = null,
      ...restFieldProps
    } = fieldProps;

    return (
      <Col key={name} {...colSize}>
        <Stack gap={1} className="mb-3">
          <Label className="fw-bold" for={name}>
            {typeof label === "function" ? label() : label}
          </Label>
          <Controller
            name={name}
            control={control}
            render={({ field: controllerField }) => {
              const commonProps = {
                controllerField,
                name,
                label,
                placeholder,
                onChange,
                ...restFieldProps,
              };

              if (render) {
                return render(commonProps)
              }

              switch (type) {
                case "select":
                  return renderSelectInput({
                    ...commonProps,
                    options,
                  });
                case "date":
                  return renderDateInput({
                    ...commonProps,
                    dateFormat,
                    showTime,
                  });
                case "infiniteSelect":
                  return fieldProps.Component ? (
                    <fieldProps.Component
                      value={controllerField.value ?? undefined}
                      onChange={(val) => controllerField.onChange(val ?? null)}
                      placeholder={
                        typeof placeholder === "function"
                          ? placeholder()
                          : placeholder || (typeof label === "function" ? label() : label)
                      }
                      allowClear
                    />
                  ) : null;
                default:
                  return renderTextInput({
                    ...commonProps,
                    inputType,
                  });
              }
            }}
          />
        </Stack>
      </Col>
    );
  };

  return (
    <Modal
      size="lg"
      isOpen={isOpen}
      toggle={handleCancel}
      centered
      backdrop="static"
      keyboard={false}
    >
      <ModalHeader dir={dir} toggle={handleCancel} tag={title}>
        <span className="fw-bold H5">{t("FILTER")}</span>
      </ModalHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody>
          <Row>{filterFields.map((field) => {
            const fieldObject = typeof field === "function" ? field(watch, setValue) : field


            return renderField(fieldObject)
          }
          )}</Row>
        </ModalBody>
        <ModalFooter dir={dir}>
          <Stack direction="horizontal" gap={2}>
            <Button
              color="secondary"
              size="xs"
              type="button"
              onClick={handleReset}
            >
              {t("RESET")}
            </Button>
            <Button
              color="danger"
              size="xs"
              type="button"
              onClick={handleCancel}
            >
              {t("CANCEL")}
            </Button>
            <Button
              color="primary"
              size="xs"
              type="button"
              onClick={handleSubmit(onSubmit)}
            >
              {t("APPLY")}
            </Button>
          </Stack>
        </ModalFooter>
      </form>
    </Modal>
  );
};
